/** URL of the default social-preview image (served from /public). */
const DEFAULT_IMAGE = '/favicon.svg';
/** Base site name shown in OG/Twitter previews. */
const SITE_NAME = 'Complex Contracts';

interface IPageMetaProps {
  /** Human-readable description used by search engines and social-preview cards. */
  description?: string;
  /** Absolute or root-relative URL of the preview image (defaults to the global brand image). */
  image?: string;
  /** Short page title appended to the site name, e.g. "Contracts". */
  title?: string;
}

/**
 * Renders document-level `<title>` and social-preview `<meta>` tags for the current page.
 *
 * React 19 automatically hoists these tags into `<head>`, so they can be placed
 * anywhere in the component tree without an external library.
 */
export const PageMeta: React.FC<IPageMetaProps> = ({ description, image = DEFAULT_IMAGE, title }) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;

  return (
    <>
      {/* Primary */}
      <title>{fullTitle}</title>
      {description && <meta content={description} name="description" />}

      {/* Open Graph (Teams, Slack, iMessage link previews) */}
      <meta content={fullTitle} property="og:title" />
      {description && <meta content={description} property="og:description" />}
      <meta content={image} property="og:image" />
      <meta content="website" property="og:type" />
      <meta content={SITE_NAME} property="og:site_name" />

      {/* Twitter / X Card */}
      <meta content="summary_large_image" name="twitter:card" />
      <meta content={fullTitle} name="twitter:title" />
      {description && <meta content={description} name="twitter:description" />}
      <meta content={image} name="twitter:image" />
    </>
  );
};
